import { useEffect, useState } from "react";
import axios from "axios";
import { Button, Spinner } from "react-bootstrap";
import { FaTrash, FaShoppingCart, FaHeart } from "react-icons/fa";
import { useCart } from "../contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import useTitle from "../hooks/useTitle";
import { toast } from "react-toastify";

export default function Wishlist() {
  useTitle("Wishlist | Astro Portal");
  const { addToCart } = useCart();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const navigate = useNavigate();

  // Fetch wishlist on mount
  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    setLoading(true);
    try {
      const authData = JSON.parse(sessionStorage.getItem("authSession"));

      // 🔒 Redirect if not logged in
      if (!authData || !authData.jwt_token || !authData.user) {
        navigate("/login?redirect=/wishlist");
        return;
      }

      const token = authData.jwt_token;

      const res = await axios.get(`${API_BASE_URL}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.status) {
        setWishlist(res.data.data || []);
      } else {
        toast.error(res.data.message || "Failed to load wishlist.");
      }
    } catch (err) {
      console.error("Wishlist Fetch Error:", err);
      if (err.response?.status === 401) {
        toast.error("Session expired, please login again.");
        sessionStorage.removeItem("authSession");
        navigate("/login");
      } else {
        toast.error("Unable to load wishlist. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // 🗑️ Remove product from wishlist
  const handleRemove = async (productId) => {
    if (!window.confirm("Remove this product from your wishlist?")) return;
    setProcessing(productId);

    try {
      const authData = JSON.parse(sessionStorage.getItem("authSession"));
      if (!authData || !authData.jwt_token) {
        navigate("/login");
        return;
      }

      const res = await axios.post(
        `${API_BASE_URL}/wishlist/remove`,
        { product_id: productId },
        {
          headers: { Authorization: `Bearer ${authData.jwt_token}` },
        }
      );

      if (res.data.status) {
        setWishlist((prev) => prev.filter((item) => item.product_id !== productId));
      } else {
        toast.error(res.data.message || "Failed to remove from wishlist.");
      }
    } catch (err) {
      console.error("Remove Error:", err);
      toast.error("Unable to remove from wishlist. Please try again.");
    } finally {
      setProcessing(null);
    }
  };

  // 🛒 Add wishlist item to cart
  const handleAddToCart = async (product) => {
    setProcessing(product.id);
    try {
      await addToCart(product.id, 1);
      toast.success("Added to cart!");
    } catch (err) {
      console.error("Add to Cart Error:", err);
      toast.error("Unable to add to cart. Please try again.");
    } finally {
      setProcessing(null);
    }
  };

  // 🕒 Loading state
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p>Loading your wishlist...</p>
      </div>
    );
  }

  // 💔 Empty wishlist
  if (wishlist.length === 0) {
    return (
      <div className="text-center py-5">
        <FaHeart size={60} color="#ccc" />
        <h4 className="mt-3">Your wishlist is empty</h4>
        <p className="text-muted">Add your favorite products to see them here.</p>
      </div>
    );
  }

  // ❤️ Wishlist items
  return (
    <div className="container my-5">
      <h2 className="fw-bold mb-4 d-flex align-items-center gap-2">
        <FaHeart className="text-danger" /> My Wishlist
      </h2>

      <div className="row g-4">
        {wishlist.map((item) => {
          const product = item.product;
          if (!product) return null;

          return (
            <div className="col-md-4 col-lg-3 col-sm-6" key={item.id}>
              <div className="card h-100 border-0 shadow-sm rounded-4">
                <div className="position-relative">
                  <img
                    src={product.product_image}
                    alt={product.name}
                    className="card-img-top rounded-top-4"
                    style={{ height: "220px", objectFit: "cover" }}
                  />

                  {/* 🗑️ Remove Button */}
                  <Button
                    variant="light"
                    size="sm"
                    className="position-absolute top-0 end-0 m-2 rounded-circle shadow-sm"
                    onClick={() => handleRemove(product.id)}
                    disabled={processing === product.id}
                  >
                    {processing === product.id ? (
                      <Spinner size="sm" />
                    ) : (
                      <FaTrash color="red" />
                    )}
                  </Button>
                </div>

                <div className="card-body text-center">
                  <h6 className="fw-bold text-truncate">{product.name}</h6>
                  <p className="text-success fw-semibold mb-1">
                    ₹{product.sale_price}
                  </p>
                  <p className="small text-muted text-truncate mb-3">
                    {product.short_description || "No description available"}
                  </p>

                  {/* 🛒 Add to Cart */}
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={() => handleAddToCart(product)}
                    disabled={processing === product.id}
                  >
                    {processing === product.id ? (
                      <>
                        <Spinner size="sm" /> Adding...
                      </>
                    ) : (
                      <>
                        <FaShoppingCart /> Add to Cart
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
